import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark, GitCompare, MapPin, Clock, Sparkles, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { Card } from "@/app/ui";
import { providerInitials, typeLabel } from "@/lib/opportunities";

export default function SavedOpportunities() {
  const [items, setItems] = useState(null);
  const [busySlug, setBusySlug] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/individual/saved-opportunities")
    .then((response) => setItems(response.data.opportunities))
    .catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const updateItem = (slug, patch) => {
    setItems((current) => current.map((item) => item.slug === slug ? { ...item, ...patch } : item));
  };

  const unsave = async (opportunity) => {
    setBusySlug(opportunity.slug);
    try {
      await api.delete(`/individual/opportunities/${opportunity.slug}/save`);
      setItems((current) => current.filter((item) => item.slug !== opportunity.slug));
      toast.success("Removed from saved opportunities.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusySlug("");
    }
  };

  const toggleCompare = async (opportunity) => {
    setBusySlug(opportunity.slug);
    try {
      const response = opportunity.in_compare
        ? await api.delete(`/individual/opportunities/${opportunity.slug}/compare`)
        : await api.post(`/individual/opportunities/${opportunity.slug}/compare`);
      updateItem(opportunity.slug, { in_compare: response.data.in_compare });
      toast.success(response.data.in_compare ? "Added to comparison." : "Removed from comparison.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setBusySlug("");
    }
  };

  const compareCount = items?.filter((item) => item.in_compare).length || 0;

  return (
    <div data-testid="saved-opportunities-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Saved opportunities</h1>
          <p className="mt-2 text-slatey">Return to the education routes you have saved and compare your shortlist.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/discover" data-testid="saved-discover-link" className="rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-ink">Discover</Link>
          <button onClick={() => navigate("/app/compare")} data-testid="saved-compare-link" disabled={compareCount === 0}
            className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50">
            Compare ({compareCount})
          </button>
        </div>
      </div>

      <p className="mt-6 text-sm font-semibold text-slatey" data-testid="saved-count">
        {items ? `${items.length} saved ${items.length === 1 ? "opportunity" : "opportunities"}` : "Loading…"}
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {items?.map((opportunity) => (
          <Card key={opportunity.id} data-testid={`saved-${opportunity.slug}`} className="p-0 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                {opportunity.provider_logo ? (
                  <img src={opportunity.provider_logo} alt="" className="h-12 w-12 rounded-2xl border border-slate-200 object-cover" />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 font-display font-bold text-cobalt">
                    {providerInitials(opportunity.provider)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  {opportunity.match != null && (
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-cobalt"><Sparkles className="h-4 w-4" /> {opportunity.match}% match</p>
                  )}
                  <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink">{opportunity.title}</h2>
                  <p className="text-sm text-slatey">{opportunity.provider}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-y-2 text-sm text-slatey sm:grid-cols-2">
                <span>{typeLabel(opportunity.opportunity_type)}</span>
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {opportunity.location}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {opportunity.duration}</span>
                <span className="flex items-center gap-1.5 font-semibold text-ink"><Wallet className="h-4 w-4 text-cobalt" /> {opportunity.fees}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 p-4">
              <Link to={`/opportunities/${opportunity.slug}`} data-testid={`saved-view-${opportunity.slug}`}
                className="flex-1 rounded-xl bg-cobalt px-4 py-3 text-center text-sm font-semibold text-white">View details</Link>
              <button onClick={() => toggleCompare(opportunity)} disabled={busySlug === opportunity.slug} data-testid={`saved-compare-${opportunity.slug}`}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold ${opportunity.in_compare ? "border-cobalt bg-cobalt/10 text-cobalt" : "border-slate-200 text-slatey hover:text-ink"}`}>
                <GitCompare className="h-4 w-4" /> {opportunity.in_compare ? "Added" : "Compare"}
              </button>
              <button onClick={() => unsave(opportunity)} disabled={busySlug === opportunity.slug} data-testid={`saved-remove-${opportunity.slug}`}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-cobalt">
                <Bookmark className="h-4 w-4 fill-current" /> Unsave
              </button>
            </div>
          </Card>
        ))}
      </div>

      {items?.length === 0 && (
        <Card data-testid="saved-empty-state" className="mt-6 text-center">
          <Bookmark className="mx-auto h-10 w-10 text-cobalt" />
          <h2 className="mt-4 font-display text-2xl font-bold text-ink">No saved opportunities yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slatey">Save opportunities from Discover to build your personal shortlist.</p>
          <Link to="/app/discover" className="mt-5 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Discover opportunities</Link>
        </Card>
      )}
    </div>
  );
}
